import { b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL15_DYNAMIC_SYMBOLS = Object.freeze({
  skateWheel11: { imagePath: "port/generated/levels_fg/level_15/skateWheel11.runtime.svg" },
  skateWheel12: { imagePath: "port/generated/levels_fg/level_15/skateWheel12.runtime.svg" },
  skateBoard1: { imagePath: "port/generated/levels_fg/level_15/skateBoard1.runtime.svg" },
  redGate: { imagePath: "port/generated/levels_fg/level_15/redGate.runtime.svg" },
  movePlatform: { imagePath: "port/generated/levels_fg/level_15/movePlatform.runtime.svg" },
  killLine: { imagePath: "port/generated/levels_fg/level_15/killLine.runtime.svg" },
  luk: { imagePath: "port/generated/levels_fg/level_15/luk.runtime.svg" },
});

const LEVEL15_DYNAMIC_DRAW_ORDER = Object.freeze([
  "redGate",
  "skateBoard1",
  "skateWheel11",
  "skateWheel12",
  "movePlatform",
  "luk",
  "killLine",
]);

function drawDynamicImage(ctx, image, x, y, rotationDeg = 0) {
  if (!image?.complete) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, -400, -400);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  if (rotationDeg) {
    ctx.rotate((rotationDeg * Math.PI) / 180);
  }
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelFifteenRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_15/1.visual.runtime.svg";
      this.level15DynamicImages = Object.fromEntries(
        Object.entries(LEVEL15_DYNAMIC_SYMBOLS).map(([name, symbol]) => {
          const image = new Image();
          image.src = symbol.imagePath;
          return [name, image];
        }),
      );
    } else {
      this.level15DynamicImages = {};
    }

    this.createAllLevelBodies();
    this.movePlatformBody = this.getBody("movePlatform");
    this.redGateBody = this.getBody("redGate");
    this.shopLeftSideBody = this.getBody("shopLeftSide");
    if (this.lastCheckNum !== 0) {
      const luk = this.getBody("luk");
      if (luk) {
        this.destroyBody(luk);
      }
    }
    this.addPrismaticJoint(this.movePlatformBody, this.ground, this.movePlatformBody.GetWorldCenter(), new b2Vec2(0, 1));
    this.addRevoluteJoint(this.getBody("skateBoard1"), this.getBody("skateWheel11"), new b2Vec2(this.getBody("skateWheel11").GetPosition().x + 3 / 30, this.getBody("skateWheel11").GetPosition().y + 3 / 30));
    this.addRevoluteJoint(this.getBody("skateBoard1"), this.getBody("skateWheel12"), new b2Vec2(this.getBody("skateWheel12").GetPosition().x + 3 / 30, this.getBody("skateWheel12").GetPosition().y + 3 / 30));
    this.movePlatformBodyDirection = 1;
    this.killLineX = this.levelData.layout.namedChildren.killLine?.x ?? 0;
  }

  levelUpdate() {
    if (this.player.y > 860 || this.hitNamedObject("killLine")) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
    if (this.hitNamedObject("redCheck") && this.redGateBody) {
      this.destroyBody(this.redGateBody);
      this.redGateBody = this.createBody("redGate", "Polygon", 1, 1, 0.2, [[[-4, -77.65], [4, -77.65], [4, 5.35], [-4, 5.35]]]);
      this.addRevoluteJoint(this.shopLeftSideBody, this.redGateBody, this.redGateBody.GetPosition());
      this.redGateBody.SetAngularVelocity(3);
    }
    const movePlatform = this.getDisplayLayout("movePlatform");
    if (movePlatform.y < 211) {
      this.movePlatformBodyDirection = 1;
    }
    if (movePlatform.y > 368) {
      this.movePlatformBodyDirection = -1;
    }
    this.movePlatformBody.SetLinearVelocity(new b2Vec2(0, 3 * this.movePlatformBodyDirection));
    if (this.killLineX < 73) {
      this.killLineX = 387;
    } else {
      this.killLineX -= 4;
    }
    if (this.levelData.layout.namedChildren.killLine) {
      this.levelData.layout.namedChildren.killLine.x = this.killLineX;
    }
    this.refreshNamedRects();
  }

  drawLevel15Bodies(ctx) {
    for (const name of LEVEL15_DYNAMIC_DRAW_ORDER) {
      if (name === "luk" && !this.getBody("luk")) {
        continue;
      }
      if (name === "killLine") {
        const killLineLayout = this.levelData.layout.namedChildren.killLine;
        if (!killLineLayout) {
          continue;
        }
        drawDynamicImage(
          ctx,
          this.level15DynamicImages.killLine,
          killLineLayout.x,
          killLineLayout.y,
          killLineLayout.rotation ?? 0,
        );
        continue;
      }
      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }
      drawDynamicImage(
        ctx,
        this.level15DynamicImages[name],
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart && this.backgroundSymbol?.layout) {
      const sym = this.backgroundSymbol;
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      ctx.save();
      ctx.translate(bx, by);
      if (sym.pngLayout) {
        const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
        const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
        ctx.drawImage(
          this.backgroundImage,
          sym.pngLayout.cropX, sym.pngLayout.cropY,
          sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
          sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
          sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
          sym.pngLayout.cropWidth * scaleX,
          sym.pngLayout.cropHeight * scaleY,
        );
      } else {
        ctx.drawImage(this.backgroundImage, sym.layout.imageOffsetX, sym.layout.imageOffsetY);
      }
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    this.drawLevel15Bodies(ctx);

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, this.goalPosition.x, this.goalPosition.y);

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
